'use client';

import { Betygsstjarnor } from '@/components/Betygsstjarnor';
import { betygText } from '@/lib/betyg';
import { useState, useEffect } from 'react';
import { visaModell } from '@/lib/fordonsnamn';
import { Link } from '@/components/LocaleLink';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { Search, Car, Package, Truck, ShieldCheck, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/stores/cart';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';
import { RecentVehiclesChips } from '@/components/RecentVehiclesChips';
import { ANTAL_TEXT, ANTAL_TEXT_EN } from '@/lib/artikelantal';

const RegistrationLookup = dynamic(
  () => import('@/components/RegistrationLookup').then((m) => ({ default: m.RegistrationLookup })),
  { ssr: false, loading: () => <RegLookupSkeleton /> }
);
const VinLookup = dynamic(
  () => import('@/components/VinLookup').then((m) => ({ default: m.VinLookup })),
  { ssr: false, loading: () => <RegLookupSkeleton /> }
);

function RegLookupSkeleton() {
  return (
    <div className="flex gap-2">
      <div className="flex-1 h-14 rounded-lg bg-white/20 animate-pulse" />
      <div className="w-32 h-14 rounded-lg bg-white/20 animate-pulse" />
    </div>
  );
}

export interface HeroProdukt { id: number; name: string; slug: string; price: number; cover_image: string | null; category_name?: string | null; quantity?: number | null }

interface Props {
  ratingValue?: number;
  reviewCount?: number;
  articleCount?: string;
  /** Mest köpta just nu (API /products/bastsaljare) — tre visas bredvid sökningen. */
  toppProdukter?: HeroProdukt[];
}

// Franska märken först — specialiteten (ägaren 2026-10-01: "Sveriges specialist på franska bilar").
const FRANSKA = [
  { namn: 'Peugeot', slug: 'peugeot' }, { namn: 'Citroën', slug: 'citroen' }, { namn: 'Renault', slug: 'renault' },
  { namn: 'Dacia', slug: 'dacia' }, { namn: 'DS', slug: 'ds' }, { namn: 'Fiat', slug: 'fiat' },
];

export function HomeHeroV2({ ratingValue, reviewCount, articleCount, toppProdukter = [] }: Props) {
  const { locale } = useLocale();
  const isSe = locale === 'sv';
  const [tab, setTab] = useState<'reg' | 'vin'>('reg');
  const [mounted, setMounted] = useState(false);
  const selectedVehicle = useCartStore((state) => state.selectedVehicle);
  const clearVehicle = useCartStore((state) => state.clearVehicle);

  useEffect(() => setMounted(true), []);

  // Förhandsstilen från <head> får INTE tas bort i samma effekt som sätter
  // mounted. setMounted schemalägger en ny rendering, så mellan borttagningen
  // och att React hinner sätta klasserna finns en frame där den höga hjälten
  // syns igen — mätt som ett skift på 0,667 där innehållet flyttades 274 px.
  // Genom att bero på `mounted` körs det här först när rätt klasser sitter.
  useEffect(() => {
    if (mounted) document.getElementById('af-hero-pre')?.remove();
  }, [mounted]);

  // Fram till 2026-09-08 returnerade komponenten det kompakta läget tidigt,
  // efter att `mounted` blivit sant. Servern skickade alltså alltid den höga
  // hjälten, och för en besökare med vald bil byttes den mot ett mycket lägre
  // block först efter hydrering — allt nedanför hoppade upp. Mätt CLS 0,73 mot
  // 0,004 utan vald bil.
  //
  // Nu ligger BÅDA varianterna i HTML:en och en inline-stil i <head> (se
  // heroScript i layout.tsx) väljer före första målningen, precis som
  // cookie-rutan gör. Ingen hydreringskonflikt: React-trädet är identiskt på
  // server och klient, bara en <style> skiljer.
  const harBil = mounted && !!selectedVehicle;

  return (
    <>
      <section className={`af-hero-vehicle ${harBil ? '' : 'hidden'} relative bg-brand text-white py-8 md:py-10`}>
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0 h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center">
                <Car className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-white/70">
                  {isSe ? 'Du letar delar för' : 'Searching parts for'}
                </p>
                {/* Höjden hålls av ikonen (48 px) och truncate ger alltid
                    en rad, så texten kan fyllas i efter hydrering utan skift. */}
                <p className="font-semibold text-lg truncate">
                  {selectedVehicle
                    ? `${selectedVehicle.manufacturer} ${visaModell(selectedVehicle.model)}${selectedVehicle.cartype ? ` · ${selectedVehicle.cartype}` : ''}`
                    : '\u00a0'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Direkt till delarna — startsidan med vald bil var annars bara ett
                  extra tryck på vägen (ägaren 2026-09-16). */}
              {selectedVehicle && (
                <Link
                  href={`/fordon/${selectedVehicle.ktype}`}
                  className="inline-flex items-center h-10 px-4 rounded-md bg-kop text-white font-semibold hover:bg-kop/90 transition-colors"
                >
                  {isSe ? 'Visa alla delar' : 'Show all parts'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              )}
              <Button
                variant="secondary"
                onClick={clearVehicle}
                className="bg-white text-brand hover:bg-white/90 shrink-0"
              >
                <X className="h-4 w-4 mr-2" />
                {isSe ? 'Byt bil' : 'Change car'}
              </Button>
            </div>
          </div>
        </div>
      </section>


      <section className={`af-hero-full ${harBil ? 'hidden' : ''} relative overflow-hidden bg-brand text-white`}>
      {/* Logotypens ränder längst ned: vit, crème, tomatröd. */}
      <div className="absolute bottom-0 left-0 right-0 h-1 flex" aria-hidden>
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-cream" />
        <div className="flex-1 bg-[var(--french-red)]" />
      </div>

      <div className="relative container mx-auto px-4 py-8 sm:py-12 md:py-14">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12 items-start">
          <div className="min-w-0">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-white/70">
              {isSe ? 'Sedan 1988 · Leverans till hela Sverige' : 'Since 1988 · Delivery across Sweden'}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl leading-[1.08] mt-3 mb-3 text-balance">
              {isSe ? <>Sveriges specialist på <span className="text-[#ffd2c6]">franska bilar</span></> : <>Sweden&apos;s specialist in <span className="text-[#ffd2c6]">French cars</span></>}
            </h1>
            <p className="text-base sm:text-lg text-white/85 max-w-xl leading-relaxed">
              {isSe
                ? 'Original- och eftermarknadsdelar till Citroën, Dacia, Fiat, Peugeot och Renault — med Sveriges djupaste sortiment. Skriv ditt regnummer så visar vi bara delar som passar.'
                : 'OE and aftermarket parts for Citroën, Dacia, Fiat, Peugeot and Renault — with the deepest range in Sweden. Enter your registration number and we only show parts that fit.'}
            </p>

            {/* Sökningen — samma funktion som förut, i varumärkets färger. */}
            <div className="mt-6 max-w-xl bg-white text-foreground rounded-2xl shadow-2xl p-4 sm:p-5">
              <div className="flex rounded-lg border overflow-hidden mb-4">
                <button type="button" onClick={() => setTab('reg')}
                  className={`flex-1 px-2 py-2.5 text-sm font-semibold transition-colors ${tab === 'reg' ? 'bg-brand text-white' : 'bg-gray-50 text-gray-600 hover:text-gray-900'}`}>
                  <span className="sm:hidden">{isSe ? 'Regnummer' : 'Reg. plate'}</span>
                  <span className="hidden sm:inline">{isSe ? 'Registreringsnummer' : 'Registration plate'}</span>
                </button>
                <button type="button" onClick={() => setTab('vin')}
                  className={`flex-1 px-2 py-2.5 text-sm font-semibold transition-colors ${tab === 'vin' ? 'bg-brand text-white' : 'bg-gray-50 text-gray-600 hover:text-gray-900'}`}>
                  <span className="sm:hidden">{isSe ? 'Chassinr (VIN)' : 'VIN'}</span>
                  <span className="hidden sm:inline">{isSe ? 'Chassinummer (VIN)' : 'Chassis number (VIN)'}</span>
                </button>
              </div>
              {tab === 'reg' ? <RegistrationLookup onSwitchToVin={() => setTab('vin')} /> : <VinLookup />}
              <div className="mt-3 pt-3 border-t text-sm">
                <Link href="/katalog" className="inline-flex items-center font-medium text-brand hover:underline">
                  {isSe ? 'Vet du redan vad du söker? Bläddra i katalogen' : 'Already know what you need? Browse the catalog'}
                  <ChevronRight className="h-4 w-4 ml-0.5" />
                </Link>
              </div>
            </div>

            <RecentVehiclesChips variant="dark" limit={4} action="navigate" />

            <div className="mt-5 flex flex-wrap gap-2">
              {FRANSKA.map((m) => (
                <Link key={m.slug} href={`/reservdelar-${m.slug}`}
                  className="rounded-full border border-white/30 px-3.5 py-1.5 text-sm font-semibold hover:bg-white hover:text-brand transition-colors">
                  {m.namn}
                </Link>
              ))}
              <Link href="#marken" className="rounded-full px-3.5 py-1.5 text-sm font-semibold text-white/80 hover:text-white">
                {isSe ? 'Alla märken →' : 'All makes →'}
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/85">
              {ratingValue && reviewCount ? (
                <a href="#omdomen" className="flex items-center gap-2 hover:text-white">
                  <span className="flex"><Betygsstjarnor betyg={ratingValue} storlek="h-4 w-4" tom="text-white/30" /></span>
                  <span className="font-semibold">{betygText(ratingValue, locale)}/5</span>
                  <span className="text-white/65">({reviewCount} {isSe ? 'omdömen på Google' : 'Google reviews'})</span>
                </a>
              ) : null}
              <span className="flex items-center gap-2"><Package className="h-4 w-4 text-cream" />{articleCount ? `${articleCount} ${isSe ? 'reservdelar' : 'parts'}` : (isSe ? `${ANTAL_TEXT}+ reservdelar` : `${ANTAL_TEXT_EN}+ parts`)}</span>
              <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-cream" />{isSe ? 'Leverans 2–5 dagar' : '2–5 day delivery'}</span>
            </div>
          </div>

          {/* Mest köpta just nu — handeln syns direkt bredvid specialisten (ägaren: "både och").
              Bara på dator: på mobil hamnar panelen under sökningen och upprepar listan strax nedanför. */}
          {toppProdukter.length > 0 && (
            <div className="hidden lg:block bg-white text-foreground rounded-2xl shadow-2xl p-4 sm:p-5">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-heading text-xl">{isSe ? 'Mest köpta just nu' : 'Best sellers right now'}</h2>
                <ShieldCheck className="h-5 w-5 text-brand shrink-0" aria-hidden />
              </div>
              <p className="text-sm text-muted-foreground mb-2">{isSe ? 'Det våra kunder handlar mest' : 'What our customers buy most'}</p>
              <ul className="divide-y">
                {toppProdukter.slice(0, 3).map((p) => (
                  <li key={p.id}>
                    <Link href={`/produkt/${p.slug}`} className="flex items-center gap-3 py-3 group">
                      <span className="relative h-16 w-20 shrink-0 rounded-lg bg-white border overflow-hidden">
                        {p.cover_image && <Image src={p.cover_image} alt={p.name} fill sizes="80px" className="object-contain p-1" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold leading-snug group-hover:text-brand line-clamp-2">{p.name}</span>
                        {p.category_name && <span className="block text-xs text-muted-foreground mt-0.5">{p.category_name}</span>}
                      </span>
                      <span className="shrink-0 font-heading text-lg tabular-nums">{Math.round(p.price).toLocaleString('sv-SE')} kr</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link href="#mest-kopta" className="mt-2 block rounded-lg bg-cream px-3 py-2.5 text-center text-sm font-semibold text-brand hover:bg-cream/80">
                {isSe ? 'Se fler populära delar ↓' : 'See more popular parts ↓'}
              </Link>
            </div>
          )}
        </div>
      </div>
      </section>
    </>
  );
}
